import React, {useReducer, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBuilding, faEnvelope, faPhone, faUnlockAlt, faUser} from '@fortawesome/free-solid-svg-icons';
import {Button, Col, Form, InputGroup, Modal, OverlayTrigger, Row, Tooltip} from '@themesberg/react-bootstrap';
import {FormattedMessage} from 'react-intl';
import {useHistory} from 'react-router-dom';

import Utils from '../services/Utils';
import API from '../services/API';
import Countries from './Countries';
import {usePlan} from '../services/PlanContext';
import {EMAIL, NAME, PHONE} from '../services/constants';

// Modale "Mon compte" — extraite de l'ancien Navbar.js et déclenchée
// depuis le dropdown profil du Sidebar.

const formReducer = (state, event) => {
  if (event.value !== undefined) {
    return {...state, [event.name]: event.value};
  }
  return event;
};

export default (props) => {
  // highlightBilling : la modale a été ouverte par la relance « informations de facturation » et non par
  // le menu profil. On souligne alors les deux seuls champs concernés, noyés au milieu du reste du
  // formulaire, et on verrouille la fermeture tant que la raison sociale n'est pas renseignée.
  const {intl, show, onClose, highlightBilling} = props;
  const history = useHistory();
  const focus = ['focus'];
  const [formData, setFormData] = useReducer(formReducer, {});
  const [initialized, setInitialized] = useState(false);
  // Pays et n° de TVA ne sont pas en localStorage : ils viennent de /admin/me/plan, qui est aussi la
  // source consommée par l'abonnement. Les modifier ici change la TVA envoyée à Stripe au checkout.
  const plan = usePlan();
  // Tant que /admin/me/plan n'a pas répondu, on ne connaît pas le pays réel : le select afficherait « FR »
  // par défaut et l'enregistrement écraserait silencieusement le vrai pays. On désactive les deux champs
  // et on ne les envoie pas (saveProfile laisse alors le pays et le n° de TVA inchangés).
  const planLoaded = !!plan.plan;

  // Re-seed form from localStorage each time the modal opens.
  React.useEffect(() => {
    if (show && !initialized) {
      setFormData({});
      setFormData({name: 'companyName', value: localStorage.getItem(NAME)});
      setFormData({name: 'email',       value: localStorage.getItem(EMAIL)});
      setFormData({name: 'phone',       value: localStorage.getItem(PHONE)});
      setFormData({name: 'countryCode', value: plan.country || 'FR'});
      setFormData({name: 'vatNumber',   value: plan.vatNumber || ''});
      setFormData({name: 'legalName',   value: plan.legalName || ''});
      setInitialized(true);
    }
    if (!show && initialized) {
      setInitialized(false);
    }
  }, [show, initialized, plan.country, plan.vatNumber, plan.legalName]);

  const handleChange = (event) => {
    setFormData({name: event.target.id, value: event.target.value});
  };

  // Modale ouverte par la relance : le chauffeur ne peut pas la refermer sans raison sociale, faute de
  // quoi la relance (qui n'a lieu qu'une fois) n'aurait servi à rien. Le n° de TVA, lui, reste
  // facultatif — beaucoup de chauffeurs en franchise en base n'en ont pas.
  //
  // On se base sur la valeur ENREGISTRÉE (plan.legalName, relu de /admin/me/plan) et non sur la saisie
  // en cours : sinon il suffisait de taper la raison sociale puis de fermer pour sortir sans rien
  // enregistrer. La seule sortie est donc « Mettre à jour », qui referme lui-même en cas de succès.
  const legalNameMissing = highlightBilling && !(plan.legalName || '').trim();

  // Toutes les sorties passent par ici : croix de l'en-tête, bouton « Fermer », clic hors modale et
  // touche Échap (neutralisés plus bas). On explique le blocage plutôt que de griser un bouton muet.
  const requestClose = () => {
    if (legalNameMissing) {
      Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'BILLING_INFO_SAVE_REQUIRED'}));
      return;
    }
    onClose && onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {...formData};
    if (!planLoaded) {
      delete payload.countryCode;
      delete payload.vatNumber;
      delete payload.legalName;
    }
    API.postProfile(history, payload).then(response => {
      if (response && response.data.success === true) {
        localStorage.setItem(NAME, formData.companyName);
        localStorage.setItem(PHONE, formData.phone);
        plan.refresh(); // le pays / n° de TVA viennent de /admin/me/plan : on le relit après sauvegarde
        if (response.data.data === 'EMAIL_VERIFICATION_SENT') {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'EMAIL_VERIFICATION_SENT'}));
        } else {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'SAVED_CHANGES'}));
          if (formData.email) {
            localStorage.setItem(EMAIL, formData.email);
          }
        }
        onClose && onClose();
      } else if (response && response.data && response.data.data === 'BILLING_INFO_REQUIRED') {
        // Filet de sécurité : le champ est déjà `required` côté navigateur.
        Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'BILLING_INFO_REQUIRED'}));
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    }).catch(error => {
      const status = error.response && error.response.status;
      const errorCode = error.response && error.response.data && error.response.data.data;
      if (status === 401) {
        Utils.logout(history, true);
      } else if (errorCode === 'EMAIL_ALREADY_USED') {
        Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'EMAIL_ALREADY_USED'}));
      } else if (errorCode === 'EMAIL_STRIPE_UPDATE_FAILED') {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'EMAIL_STRIPE_UPDATE_FAILED'}));
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  };

  return (
    <Modal as={Modal.Dialog} centered show={show} onHide={requestClose}
           backdrop={legalNameMissing ? 'static' : true} keyboard={!legalNameMissing}>
      <Modal.Header>
        <Modal.Title className="h6"><FormattedMessage id="MY_ACCOUNT"/></Modal.Title>
        <Button variant="close" aria-label="Close" onClick={requestClose}/>
      </Modal.Header>
      <Modal.Body>
        <Form id="account-form" onSubmit={handleSubmit}>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="BUSINESS_NAME"/></Form.Label>
                <InputGroup>
                  <InputGroup.Text><FontAwesomeIcon icon={faUser}/></InputGroup.Text>
                  <OverlayTrigger trigger={focus} overlay={<Tooltip><FormattedMessage id="TOOLTIP_COMPANY_NAME"/></Tooltip>}>
                    <Form.Control required type="text" id="companyName"
                                  onChange={handleChange} value={formData.companyName || ''}/>
                  </OverlayTrigger>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="EMAIL_ADDRESS"/></Form.Label>
                <InputGroup>
                  <InputGroup.Text><FontAwesomeIcon icon={faEnvelope}/></InputGroup.Text>
                  <Form.Control required type="email" id="email"
                                onChange={handleChange} value={formData.email || ''}/>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
          <br/>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="PHONE"/></Form.Label>
                <InputGroup>
                  <InputGroup.Text><FontAwesomeIcon icon={faPhone}/></InputGroup.Text>
                  <OverlayTrigger trigger={focus} overlay={<Tooltip><FormattedMessage id="TOOLTIP_PHONE"/></Tooltip>}>
                    <Form.Control type="tel" id="phone"
                                  onChange={handleChange} value={formData.phone || ''}/>
                  </OverlayTrigger>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
          <br/>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="COUNTRY"/></Form.Label>
                <Countries id="countryCode" value={formData.countryCode || 'FR'} onChange={handleChange}
                           disabled={!planLoaded} intl={intl}/>
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group className={highlightBilling ? 'cc-field-highlight' : undefined}>
                <Form.Label><FormattedMessage id="VAT_NUMBER"/></Form.Label>
                <InputGroup>
                  <InputGroup.Text><FontAwesomeIcon icon={faBuilding}/></InputGroup.Text>
                  <Form.Control type="text" id="vatNumber" disabled={!planLoaded}
                                onChange={handleChange} value={formData.vatNumber || ''}/>
                </InputGroup>
                {highlightBilling && (
                  <Form.Text className="cc-field-highlight-hint"><FormattedMessage id="VAT_REMINDER_HINT"/></Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
          <br/>
          {/* Raison sociale portée par la facture d'abonnement. Distincte du nom commercial ci-dessus,
              qui est ce que voient les clients du chauffeur. L'adresse de facturation ne figure pas ici :
              elle est collectée et conservée par Stripe, et se modifie depuis le portail de facturation.
              Comme le pays et le n° de TVA, elle vient de /admin/me/plan : tant qu'il n'a pas répondu on
              ne l'envoie pas, pour ne pas écraser silencieusement ce qui est en base. */}
          <Row>
            <Col md={12} className="mb-3">
              <Form.Group className={highlightBilling ? 'cc-field-highlight' : undefined}>
                <Form.Label><FormattedMessage id="LEGAL_NAME"/></Form.Label>
                <InputGroup>
                  <InputGroup.Text><FontAwesomeIcon icon={faBuilding}/></InputGroup.Text>
                  <Form.Control required={planLoaded} type="text" id="legalName" disabled={!planLoaded}
                                onChange={handleChange} value={formData.legalName || ''}/>
                </InputGroup>
                <Form.Text className="text-muted"><FormattedMessage id="LEGAL_NAME_HELP"/></Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <br/>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label><FormattedMessage id="NEW_PASSWORD"/></Form.Label>
                <InputGroup>
                  <InputGroup.Text><FontAwesomeIcon icon={faUnlockAlt}/></InputGroup.Text>
                  <OverlayTrigger trigger={focus} overlay={<Tooltip><FormattedMessage id="TOOLTIP_PASSWORD"/></Tooltip>}>
                    <Form.Control type="password" id="newPassword"
                                  onChange={handleChange} value={formData.newPassword || ''}/>
                  </OverlayTrigger>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
          <br/>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" type="submit" form="account-form">
          <FormattedMessage id="UPDATE"/>
        </Button>
        <Button variant="link" className="text-gray ms-auto" onClick={requestClose}>
          <FormattedMessage id="CLOSE"/>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
